/** "Materialised from" provenance + the per-aspect managed switches.
 *
 *  Shown on the notification editor only when the notification has a canned
 *  binding. Four independent switches, each naming what it covers, because the
 *  consequence is not obvious from the word "managed": while an aspect is on,
 *  saving an edit to it is REFUSED, and a refresh reapplies the pack's version.
 *
 *  Turning one off is a one-way door in practice — the pack stops updating that
 *  aspect — so the switch says so before it is flipped, not after.
 */
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { MANAGED_LABELS, type ManagedAspectKey } from '@/api/cannedNotifications';
import * as notificationsApi from '@/api/notifications';
import type { CannedRef, NotificationCore, NotificationDetail } from '@/api/notifications';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatRelativeTime } from '@/utils/format';

interface Props {
  notificationId: number;
  canned: CannedRef;
  core: NotificationCore;
  canManage: boolean;
  canRefresh: boolean;
  /** Unsaved edits in the editor. A refresh rewrites the notification on the
   *  server, so it is blocked while the draft is dirty rather than silently
   *  discarding work. */
  dirty?: boolean;
  /** A switch was flipped: the server returned a fresh aggregate, but the
   *  editor's draft is still valid — update the cache, do not reset it. */
  onManagedChanged: (detail: NotificationDetail) => void;
  /** The pack rewrote the notification: the draft IS stale, reload it. */
  onRefreshed: () => void;
}

function flagValue(core: NotificationCore, key: ManagedAspectKey): boolean {
  return Boolean(core[`managed_${key}`]);
}

export function CannedManagedPanel({
  notificationId,
  canned,
  core,
  canManage,
  canRefresh,
  dirty = false,
  onManagedChanged,
  onRefreshed,
}: Props) {
  const [error, setError] = useState<string | null>(null);

  const setManaged = useMutation({
    mutationFn: (body: Partial<Record<ManagedAspectKey, boolean>>) =>
      notificationsApi.setManaged(notificationId, body),
    onSuccess: (detail) => {
      setError(null);
      onManagedChanged(detail);
    },
    onError: (e) =>
      setError((e as { body?: { detail?: string } })?.body?.detail ?? 'Could not change it.'),
  });

  const refresh = useMutation({
    mutationFn: () => notificationsApi.refreshCanned(notificationId),
    onSuccess: () => {
      setError(null);
      onRefreshed();
    },
    onError: (e) =>
      setError((e as { body?: { detail?: string } })?.body?.detail ?? 'The refresh could not run.'),
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Materialised from the notification pack</Typography>
        {/* `/notifications/canned` takes no params, so the archetype cannot be
            deep-linked — the codename is at least copyable for a search. */}
        <ValueActions chip actions={[act.copy(canned.codename, 'Copy archetype codename')]}>
          <Chip size="small" variant="outlined" label={canned.codename} />
        </ValueActions>
        {canned.pack_version && (
          <ValueActions chip actions={[act.copy(canned.pack_version, 'Copy pack version')]}>
            <Chip size="small" variant="outlined" label={canned.pack_version} />
          </ValueActions>
        )}
        {canned.stale && (
          <Tooltip title="The pack's version of this message has changed since this one was built. Refresh to take the update on every aspect still managed.">
            <Chip size="small" color="warning" label="pack has moved on" />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {canRefresh && (
          <Tooltip
            title={
              dirty
                ? 'Save or discard your changes first — a refresh reloads the notification from the server.'
                : 'Reapply the pack to every aspect still managed.'
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refresh.mutate()}
                disabled={dirty || refresh.isPending}
              >
                Refresh from pack
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Built {formatRelativeTime(canned.materialised_ts)}
        {canned.refreshed_ts ? `, last refreshed ${formatRelativeTime(canned.refreshed_ts)}` : ''}.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Stack sx={{ mt: 1 }}>
        {MANAGED_LABELS.map(({ key, label, help }) => {
          const on = flagValue(core, key);
          return (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  size="small"
                  checked={on}
                  disabled={!canManage || setManaged.isPending}
                  onChange={() => setManaged.mutate({ [key]: !on })}
                />
              }
              label={
                <span>
                  <strong>{label}</strong>{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {help}{' '}
                    {on
                      ? 'The pack owns it — edits here are refused.'
                      : 'Yours — the pack no longer updates it.'}
                  </Typography>
                </span>
              }
            />
          );
        })}
      </Stack>
    </Paper>
  );
}
